import { deepLinks } from "../navigation/deep-links";
import { type AreaKey, isAreaOpenable, type Offering } from "../navigation/offering";

// The link from a failure, a job or an audit row to the record it is about (CHT-772). One table says, for each record type the API names, which
// screen opens it and which area gates that screen. A link is shown ONLY when that area opens for the staff member (the offering: their roles
// and the features the API switched on): a role that cannot open the screen sees the type and a short id as plain text, never a link that
// ends in a refusal. The page and the API check the
// role and the relationship again; a link grants nothing.

type Spec = Readonly<{
  /** The area that gates the screen. */
  area: AreaKey;
  label: string;
  href: (id: string) => string;
}>;

const specs: Readonly<Record<string, Spec>> = {
  payment_request: { area: "payments", label: "Payment", href: deepLinks.payment },
  concierge_request: { area: "requests", label: "Request", href: deepLinks.request },
  conversation: { area: "concierge", label: "Conversation", href: deepLinks.conversation },
  import_job: { area: "imports", label: "Import", href: deepLinks.catalogImport },
  trip: { area: "trips", label: "Trip", href: deepLinks.tripItinerary },
  customer: { area: "customers", label: "Customer", href: deepLinks.customerSupport },
  catalog_item: {
    area: "catalog",
    label: "Catalog item",
    href: deepLinks.catalogItem,
  },
  // A job intent has no page of its own: the failed jobs are listed on Operations.
  background_intent: { area: "operations", label: "Job", href: deepLinks.failedJobs },
  background_job_intent: {
    area: "operations",
    label: "Job",
    href: deepLinks.failedJobs,
  },
  // An open exception is listed and resolved on the Diagnostics page.
  payment_reconciliation_exception: {
    area: "operations",
    label: "Payment exception",
    href: deepLinks.paymentExceptions,
  },
};

/** The record types that have a screen at all (for a test of the table). */
export const linkableRecordTypes: readonly string[] = Object.keys(specs);

export type RecordLinkTarget = Readonly<{ label: string; href: string | null }>;

/**
 * Where a record opens for this offering. `href` is `null` when the area does not open (the role cannot open it, or its feature is off:
 * the screen would answer "Not available here", CHT-1981), or the type has no screen (an AI run); the `label` is still the plain word for the type.
 */
export function recordLinkFor(type: string, id: string, offering: Offering): RecordLinkTarget {
  const spec = specs[type];
  if (spec === undefined) return { label: type.replaceAll("_", " "), href: null };
  return { label: spec.label, href: isAreaOpenable(spec.area, offering) ? spec.href(id) : null };
}
